import React, { useCallback, memo } from 'react';
import Modal from 'Common/Components/Modal';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';

export interface SettingsFormValues {
  name: string;
  description: string;
}

interface SettingsModalProps {
  open: boolean;
  title: string;
  initialValues?: Partial<SettingsFormValues>;
  onClose: () => void;
  onSubmit: (values: SettingsFormValues, formik: FormikHelpers<SettingsFormValues>) => Promise<void> | void;
  submitLabel?: string;
  submittingLabel?: string;
  className?: string;
}

const defaultInitialValues: SettingsFormValues = {
  name: '',
  description: '',
};

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
  description: Yup.string().trim(),
});

type FieldConfig = {
  name: keyof SettingsFormValues;
  label: string;
  required?: boolean;
  placeholder?: string;
  as?: 'input' | 'textarea';
};

const fields: FieldConfig[] = [
  {
    name: 'name',
    label: 'Name',
    required: true,
    placeholder: 'Enter name',
    as: 'input',
  },
  {
    name: 'description',
    label: 'Description',
    placeholder: 'Enter description',
    as: 'input',
  },
];

const FormField: React.FC<{ config: FieldConfig }> = ({ config }) => {
  const { name, label, required, placeholder, as = 'input' } = config;
  const id = name;
  const baseClass =
    'form-input border-slate-200 dark:border-zink-500 w-full resize-none';
  const component = as === 'textarea' ? 'textarea' : 'input';

  return (
    <div className="flex items-start gap-4">
      <label htmlFor={id} className="text-base font-medium w-1/3 pt-2">
        {label}
        {required && (
          <span
            className="text-red-500 ml-1 cursor-pointer"
            title="This field is mandatory"
          >
            *
          </span>
        )}
      </label>
      <div className="flex-1">
        <Field
          as={component}
          id={id}
          // casting name to string for Field
          name={name as string}
          placeholder={placeholder}
          className={baseClass}
        />
        <ErrorMessage
          name={name as string}
          component="div"
          className="text-red-500 text-sm mt-1"
        />
      </div>
    </div>
  );
};

const SettingsModal: React.FC<SettingsModalProps> = memo(
  ({
    open,
    title,
    onClose,
    onSubmit,
    initialValues,
    submitLabel,
    submittingLabel = 'Saving...',
    className = '',
  }) => {
    const mergedInitialValues: SettingsFormValues = {
      ...defaultInitialValues,
      ...initialValues,
    };

    const handleSubmit = useCallback(
      async (
        values: SettingsFormValues,
        formik: FormikHelpers<SettingsFormValues>
      ) => {
        await onSubmit(values, formik);
      },
      [onSubmit]
    );

    return (
      <Modal
        show={open}
        onHide={onClose}
        modal-center="true"
        className={`fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4 ${className}`}
        dialogClassName="w-[calc(100vw-2rem)] mx-4 sm:w-[40rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
        >
          <Modal.Title className="text-16">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] overflow-y-auto p-4">
          <Formik
            initialValues={mergedInitialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting, dirty, isValid, resetForm }) => (
              <div className="m-auto">
                <Form>
                  <div className="space-y-5">
                    {fields.map(f => (
                      <FormField key={f.name} config={f} />
                    ))}
                  </div>

                  <div className="flex justify-end items-end gap-2 mt-10">
                    <button
                      type="button"
                      className="bg-white text-slate-500 btn hover:text-slate-500 hover:bg-slate-100 focus:text-slate-500 focus:bg-slate-100 active:text-slate-500 active:bg-slate-100 dark:bg-zink-600 dark:hover:bg-slate-500/10 dark:focus:bg-slate-500/10 dark:active:bg-slate-500/10"
                      onClick={() => {
                        resetForm();
                        onClose();
                      }}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="text-white btn bg-custom-500 disabled:opacity-60 disabled:cursor-not-allowed"
                      disabled={isSubmitting || !dirty || !isValid}
                    >
                      {isSubmitting
                        ? submittingLabel
                        : submitLabel || title || 'Save'}
                    </button>
                  </div>
                </Form>
              </div>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    );
  }
);

SettingsModal.displayName = 'SettingsModal';

export default SettingsModal;